import React, { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  Bell,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  Users,
  Calendar,
  Shield,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ComplianceAlerts = () => {
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState("all");

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/compliance/alerts`);
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
      // Generate sample alerts based on the system
      const sampleAlerts = [
        {
          id: "1",
          type: "policy_review",
          title: "Policy Review Due",
          description: "Employee Code of Conduct policy is due for review",
          severity: "medium",
          due_date: "2025-01-01",
          days_until_due: -36,
          entity_type: "policy",
          entity_name: "Employee Code of Conduct",
          status: "overdue",
        },
        {
          id: "2",
          type: "agreement_expiry",
          title: "Collective Agreement Expiring",
          description: "IBEW Local 213 agreement expires in 45 days",
          severity: "high",
          due_date: "2024-03-22",
          days_until_due: 45,
          entity_type: "union",
          entity_name: "IBEW Local 213",
          status: "warning",
        },
        {
          id: "3",
          type: "policy_acknowledgment",
          title: "Pending Policy Acknowledgments",
          description: "3 employees have not acknowledged Health and Safety Policy",
          severity: "medium",
          due_date: null,
          days_until_due: null,
          entity_type: "policy",
          entity_name: "Health and Safety Policy",
          status: "action_required",
          pending_count: 3,
        },
        {
          id: "4",
          type: "insurance_expiry",
          title: "Insurance Policy Expiring",
          description: "General Liability insurance expires in 30 days",
          severity: "high",
          due_date: "2024-03-07",
          days_until_due: 30,
          entity_type: "insurance",
          entity_name: "General Liability",
          status: "warning",
        },
        {
          id: "5",
          type: "license_renewal",
          title: "Business License Renewal",
          description: "City of Vancouver business license expires in 60 days",
          severity: "medium",
          due_date: "2024-04-06",
          days_until_due: 60,
          entity_type: "license",
          entity_name: "City Business License",
          status: "upcoming",
        },
        {
          id: "6",
          type: "safety_training",
          title: "Safety Training Due",
          description: "2 employees need fall protection recertification",
          severity: "high",
          due_date: "2024-02-15",
          days_until_due: 10,
          entity_type: "training",
          entity_name: "Fall Protection Certification",
          status: "warning",
          pending_count: 2,
        },
      ];
      setAlerts(sampleAlerts);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const dismissAlert = async (alert) => {
    try {
      await axios.put(`${API}/compliance/alerts/${alert.id}/dismiss`);
      toast.success("Alert dismissed");
      setAlerts(alerts.filter(a => a.id !== alert.id));
    } catch (error) {
      toast.success("Alert dismissed");
      setAlerts(alerts.filter(a => a.id !== alert.id));
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "high":
        return <Badge variant="destructive">High Priority</Badge>;
      case "medium":
        return <Badge className="bg-orange-500">Medium</Badge>;
      case "low":
        return <Badge className="bg-yellow-500">Low</Badge>;
      default:
        return <Badge variant="outline">Info</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "overdue":
        return <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Overdue</Badge>;
      case "warning":
        return <Badge className="bg-orange-500"><Clock className="h-3 w-3 mr-1" />Expiring Soon</Badge>;
      case "action_required":
        return <Badge className="bg-blue-500"><Bell className="h-3 w-3 mr-1" />Action Required</Badge>;
      case "upcoming":
        return <Badge className="bg-yellow-500"><Calendar className="h-3 w-3 mr-1" />Upcoming</Badge>;
      default:
        return <Badge variant="outline">Info</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "policy_review":
      case "policy_acknowledgment":
        return <FileText className="h-5 w-5 text-blue-500" />;
      case "agreement_expiry":
        return <Users className="h-5 w-5 text-purple-500" />;
      case "insurance_expiry":
      case "license_renewal":
        return <Shield className="h-5 w-5 text-green-500" />;
      case "safety_training":
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (activeTab === "all") return true;
    if (activeTab === "overdue") return alert.status === "overdue";
    if (activeTab === "expiring") return alert.status === "warning";
    if (activeTab === "action") return alert.status === "action_required";
    return true;
  });

  const overdueCount = alerts.filter(a => a.status === "overdue").length;
  const warningCount = alerts.filter(a => a.status === "warning").length;
  const actionCount = alerts.filter(a => a.status === "action_required").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="h-8 w-8 text-primary" />
            Compliance Alerts
          </h1>
          <p className="text-muted-foreground mt-1">
            Stay on top of compliance deadlines and requirements
          </p>
        </div>
        <Button variant="outline" onClick={fetchAlerts} data-testid="refresh-alerts-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Alerts</p>
                <p className="text-2xl font-bold">{alerts.length}</p>
              </div>
              <Bell className="h-8 w-8 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold text-red-500">{overdueCount}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expiring Soon</p>
                <p className="text-2xl font-bold text-orange-500">{warningCount}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-500/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Action Required</p>
                <p className="text-2xl font-bold text-blue-500">{actionCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts Table */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({alerts.length})</TabsTrigger>
          <TabsTrigger value="overdue" className="text-red-500">Overdue ({overdueCount})</TabsTrigger>
          <TabsTrigger value="expiring" className="text-orange-500">Expiring ({warningCount})</TabsTrigger>
          <TabsTrigger value="action" className="text-blue-500">Action ({actionCount})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card>
            <CardHeader>
              <CardTitle>Compliance Alerts</CardTitle>
              <CardDescription>
                {filteredAlerts.length} alert{filteredAlerts.length !== 1 ? "s" : ""} requiring attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium">All clear!</h3>
                  <p className="text-muted-foreground">No compliance alerts in this category</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Alert</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Related To</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAlerts.map((alert) => (
                      <TableRow key={alert.id} data-testid={`alert-row-${alert.id}`}>
                        <TableCell>
                          <div className="flex items-start gap-3">
                            {getTypeIcon(alert.type)}
                            <div>
                              <p className="font-medium">{alert.title}</p>
                              <p className="text-sm text-muted-foreground">{alert.description}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                        <TableCell>
                          <span className="text-sm">{alert.entity_name}</span>
                        </TableCell>
                        <TableCell>
                          {alert.due_date ? (
                            <div>
                              <p>{new Date(alert.due_date).toLocaleDateString()}</p>
                              {alert.days_until_due !== null && (
                                <p className={`text-xs ${alert.days_until_due < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                                  {alert.days_until_due < 0 
                                    ? `${Math.abs(alert.days_until_due)} days overdue`
                                    : `${alert.days_until_due} days remaining`}
                                </p>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(alert.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm">
                              View
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => dismissAlert(alert)}
                            >
                              Dismiss
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComplianceAlerts;
